import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BackupRestorationStateChangedEnvelope,
  BackupStateChangedEnvelope,
  CreateBackupResponse,
  ListBackupsOptions,
  ListBackupsResponse,
  ListRestorationsOptions,
  ListRestorationsResponse,
  RestorationCollection,
  RestoreBackupResponse,
  RestorePartialBackupResponse,
  createBackup as universalCreateBackup,
  deleteBackup as universalDeleteBackup,
  listBackups as universalListBackups,
  listRestorations as universalListRestorations,
  restoreBackup as universalRestoreBackup,
  restorePartialBackup as universalRestorePartialBackup,
} from './data-v2-backup-backups.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function createBackup(httpClient: HttpClient): CreateBackupSignature {
  return () =>
    universalCreateBackup(
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBackupSignature {
  /**
   * Creates an on-demand backup of live content in a site's collections.
   *
   *
   * By default, all of the site's collections are included in the backup. For a partial backup, specify which collections to include in the `backup.collections` parameter.
   *
   * The process of creating a backup takes time.
   * You can check whether a backup has completed successfully with List Backups.
   *
   * You can store up to 3 on-demand backups for each site.
   * If 3 on-demand backups already exist, the oldest existing on-demand backup for the site is deleted when a new one is created. Automated backups are not affected.
   */
  (): Promise<
    NonNullablePaths<
      CreateBackupResponse,
      | `backup._id`
      | `backup.status`
      | `backup.type`
      | `backup.collections`
      | `backup.collections.${number}._id`,
      5
    >
  >;
}

export function listBackups(httpClient: HttpClient): ListBackupsSignature {
  return (options?: ListBackupsOptions) =>
    universalListBackups(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListBackupsSignature {
  /**
   * Retrieves a list of all backups for a site.
   *
   * Results are sorted by requested date, with the newest first.
   *
   * You can use this method to check whether a backup initiated with Create Backup has been completed successfully.
   */
  (options?: ListBackupsOptions): Promise<
    NonNullablePaths<
      ListBackupsResponse,
      | `backups`
      | `backups.${number}._id`
      | `backups.${number}.status`
      | `backups.${number}.type`,
      4
    >
  >;
}

export function restoreBackup(httpClient: HttpClient): RestoreBackupSignature {
  return (backupId: string) =>
    universalRestoreBackup(
      backupId,
      // @ts-ignore
      { httpClient }
    );
}

interface RestoreBackupSignature {
  /**
   * Restores all data from a backup.
   *
   * The process of restoring data from a backup takes time.
   * You can check whether your restoration has completed successfully with List Restorations.
   * @param - ID of backup to be restored.
   */
  (backupId: string): Promise<
    NonNullablePaths<
      RestoreBackupResponse,
      | `restoration._id`
      | `restoration.backup._id`
      | `restoration.backup.status`
      | `restoration.backup.type`
      | `restoration.backup.collections`
      | `restoration.backup.collections.${number}._id`
      | `restoration.status`
      | `restoration.restorationCollections`
      | `restoration.restorationCollections.${number}.dataCollectionId`
      | `restoration.restorationCollections.${number}.restoreDestination.dataCollectionId`,
      6
    >
  >;
}

export function restorePartialBackup(
  httpClient: HttpClient
): RestorePartialBackupSignature {
  return (backupId: string, restorationCollections: RestorationCollection[]) =>
    universalRestorePartialBackup(
      backupId,
      restorationCollections,
      // @ts-ignore
      { httpClient }
    );
}

interface RestorePartialBackupSignature {
  /**
   * Restores specific collections from a backup.
   *
   * The process of restoring data from a backup takes time.
   * You can check whether your restoration has completed successfully with List Restorations.
   * @param - ID of backup to be restored.
   * @param - **Required.** Collections to be restored.
   *
   * Note: If collections have a multi-reference relationship,
   * the preexisting references will be restored if at least one of those collections are restored.
   */
  (backupId: string, restorationCollections: RestorationCollection[]): Promise<
    NonNullablePaths<
      RestorePartialBackupResponse,
      | `restoration._id`
      | `restoration.backup._id`
      | `restoration.backup.status`
      | `restoration.backup.type`
      | `restoration.backup.collections`
      | `restoration.backup.collections.${number}._id`
      | `restoration.status`
      | `restoration.restorationCollections`
      | `restoration.restorationCollections.${number}.dataCollectionId`
      | `restoration.restorationCollections.${number}.restoreDestination.dataCollectionId`,
      6
    >
  >;
}

export function listRestorations(
  httpClient: HttpClient
): ListRestorationsSignature {
  return (options?: ListRestorationsOptions) =>
    universalListRestorations(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListRestorationsSignature {
  /**
   * Retrieves a list of all data restorations from backups.
   *
   * Results are sorted by requested date, with the newest first.
   *
   * You can use this method to check whether a restoration initiated with Restore Backup has been completed successfully.
   */
  (options?: ListRestorationsOptions): Promise<
    NonNullablePaths<
      ListRestorationsResponse,
      | `restorations`
      | `restorations.${number}._id`
      | `restorations.${number}.backup._id`
      | `restorations.${number}.backup.status`
      | `restorations.${number}.backup.type`
      | `restorations.${number}.status`,
      5
    >
  >;
}

export function deleteBackup(httpClient: HttpClient): DeleteBackupSignature {
  return (backupId: string) =>
    universalDeleteBackup(
      backupId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBackupSignature {
  /**
   * Deletes a backup.
   *
   * The process of deleting a backup takes time.
   * You can check whether a backup has been deleted successfully with List Backups.
   * @param - ID of the backup to be deleted.
   */
  (backupId: string): Promise<void>;
}

export const onBackupStateChanged = EventDefinition(
  'wix.data.v2.backup_backup_state_changed',
  true,
  (event: BackupStateChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.backup.requestedDate' },
            { path: 'data.backup.startedDate' },
            { path: 'data.backup.finishedDate' },
            { path: 'data.backup.deletedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BackupStateChangedEnvelope>();
export const onBackupRestorationStateChanged = EventDefinition(
  'wix.data.v2.backup_restoration_state_changed',
  true,
  (event: BackupRestorationStateChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.restoration.requestedDate' },
            { path: 'data.restoration.startedDate' },
            { path: 'data.restoration.finishedDate' },
            { path: 'data.restoration.backup.requestedDate' },
            { path: 'data.restoration.backup.startedDate' },
            { path: 'data.restoration.backup.finishedDate' },
            { path: 'data.restoration.backup.deletedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<BackupRestorationStateChangedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Backup,
  BackupRestorationStateChangedEnvelope,
  BackupStateChanged,
  BackupStateChangedEnvelope,
  BaseEventMetadata,
  CancelBackupRequest,
  CancelBackupResponse,
  Collection,
  CreateBackupRequest,
  CreateBackupResponse,
  DeleteAllRequest,
  DeleteBackupRequest,
  DeleteBackupResponse,
  DisableInstanceRequest,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EnableInstanceRequest,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FailRestorationRequest,
  GetBackupMetadataRequest,
  GetBackupMetadataResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListBackupsOptions,
  ListBackupsRequest,
  ListBackupsResponse,
  ListRestorationsOptions,
  ListRestorationsRequest,
  ListRestorationsResponse,
  MessageEnvelope,
  MigrateNamespaceRequest,
  MigrateNamespaceResponse,
  MoveRequest,
  Paging,
  PagingMetadataV2,
  RebuildRequest,
  RemoveDeletedRequest,
  Restoration,
  RestorationCollection,
  RestorationStateChanged,
  RestorationStatus,
  RestoreBackupRequest,
  RestoreBackupResponse,
  RestoreDestination,
  RestoreInfo,
  RestorePartialBackupRequest,
  RestorePartialBackupResponse,
  Status,
  TakeBackupRequest,
  Type,
  UpdateBackupMetadataRequest,
  WebhookIdentityType,
} from './data-v2-backup-backups.universal.js';
